import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';

export default function HeroSection() {
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => textRef.current?.classList.add('visible'), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-dark">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/ef25b9ceb5919038017dd6d074e4f389.jpg"
          alt="Premium furniture collections - Eclectic Home furniture store Las Vegas"
          title="Eclectic Home - Modern Furniture Collections Las Vegas"
          className="w-full h-full object-cover object-center"
        />
        {/* Layered overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-dark/90 via-dark/55 to-dark/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark/50 via-transparent to-dark/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-12">
        <div ref={textRef} className="fade-up">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="w-10 h-px bg-gold" />
            <span className="text-gold text-[11px] font-bold uppercase tracking-[0.3em]">
              Las Vegas&apos; Premier Furniture Store
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-serif font-bold text-white leading-[0.95] mb-8" style={{ fontSize: 'clamp(52px, 7vw, 96px)' }}>
            Furniture That
            <br />
            <em className="text-gold not-italic">Defines</em> How
            <br />
            You Live.
          </h1>

          <p className="text-white/65 text-lg leading-relaxed mb-10 max-w-xl font-light">
            500+ handpicked pieces for every room. Exceptional craftsmanship, 
            curated for the modern home — delivered with white-glove care.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-start gap-4 mb-16">
            <Link
              to="/collections"
              className="bg-gold text-white px-9 py-4 rounded-lg text-sm font-bold uppercase tracking-widest hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              Shop Collection
            </Link>
            <Link
              to="/collections"
              className="flex items-center gap-3 border border-white/30 text-white px-9 py-4 rounded-lg text-sm font-semibold uppercase tracking-widest hover:border-white/70 hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap"
            >
              Browse Categories
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </Link>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-12 pt-8 border-t border-white/10">
            {[
              { value: '500+', label: 'Curated Pieces' },
              { value: '20 yrs', label: 'In Business' },
              { value: '5,000+', label: 'Happy Homes' },
              { value: 'Free', label: 'Design Consult' },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-serif text-2xl font-bold text-white leading-none mb-1">{s.value}</div>
                <div className="text-white/35 text-[10px] uppercase tracking-[0.18em]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating accent card — right side visual interest */}
      <div className="absolute bottom-16 right-12 hidden xl:block">
        <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-5 w-52">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold/20">
              <i className="ri-star-fill text-gold text-sm" />
            </div>
            <div>
              <div className="text-white text-xs font-bold">Top Rated</div>
              <div className="text-white/40 text-[10px]">Google Reviews</div>
            </div>
          </div>
          <div className="flex gap-0.5 mb-2">
            {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-gold text-xs" />)}
          </div>
          <p className="text-white/60 text-xs leading-relaxed">
            "Transformed our home completely. Absolutely beautiful pieces."
          </p>
          <div className="mt-2 text-white/30 text-[10px] font-semibold">— Sarah M., Henderson NV</div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
        <div className="w-px h-12 bg-gradient-to-b from-white/60 to-transparent" />
        <span className="text-white/40 text-[9px] uppercase tracking-[0.25em]">Scroll</span>
      </div>
    </section>
  );
}
